import PropTypes from 'prop-types'
import { LockedValue } from '../../../components/billing/RoLocked'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { ConfirmModal, ItemPerPageSelector } from '@components/common'
import { EmailForm } from '@components/email'
import { AssigneeAvatarPicker } from '@components/assigned-user'
import { SubStatusPicker } from '@components/sub-status-picker'
import { Checkbox } from '@components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { QueueCard } from '@components/queue-card'
import { QueueSelectionRibbon } from '@components/queue-selection-ribbon'
import { CreateAppointmentForm } from '@components/scheduler/schedule-calendar/CreateAppointmentForm'
import { buildAppointmentPrefillFromQuote } from '@components/scheduler/schedule-calendar/helper'
import { MobileFilterDrawer } from '@components/mobile-filter-drawer'
import { SortButton, SortableTableHead, nextSort, sortFromQuery, sortRequestParams, writeSortToQuery } from '@components/table-sort'
import { AdasBox, AdasPagination, AdasPaper, AdasStack } from '@components/wrapper-components'
import { AdasCircularProgress } from '@components/wrapper-components'
import { AlertTriangle, Calendar, CarFront, Download, Mail, PauseCircle, Plus, Printer, Search, ThumbsDown, Wrench, X } from 'lucide-react'
import adasIntelligenceMark from '../../../components/navigation-bar/navigation-bar/Blue_trimmed_logo.svg'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { closeQuote, getQuoteEmails, getQuotePdf, getQuotelist, transformToWO } from '../../../api/api'
import { BUTTONS_DISPLAY_LABEL, REDIRECT_URLS, getQuoteSummaryUrl, getWorkOrderSummaryUrl } from '../../../constants'
import { LOST_SUB_STATUSES, StatusEnum, SubStatusEnum } from '../../../enums/enums'
import { scrollPageTop, useScrollTop } from '../../../utils/useScrollTop'
import { currencyFormatter } from '../../../utils/utils'
import { Logger } from '../../../logger'
import SearchWithSelect from '@components/search-with-select/SearchWithSelect'
import { useSearchFilters } from '@hooks/useSearchFilters'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useExistingAppointmentGuard } from '@hooks/useExistingAppointmentGuard'
import { useModuleAccess } from '@hooks/useModuleAccess'
import { useMessageStore } from '@caradasstore/MessageStore'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { handlePdf, PDF_ACTION } from '../../../utils/pdfActions'

// Sortable columns (keys whitelisted by the backend, QwiListSort). The Quote column sorts by quote #.
const QUOTE_SORT_OPTIONS = [
  { value: 'referenceNumber', label: 'Quote #' },
  { value: 'client', label: 'Client' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'status', label: 'Status' },
  { value: 'amount', label: 'Amount' },
  { value: 'createdAt', label: 'Created' }
]
const QUOTE_SORT_KEYS = QUOTE_SORT_OPTIONS.map((o) => o.value)

// CAR-3491 — Sub-status filter chip row above the Quotes Queue.
const SUB_STATUS_FILTER_ICONS = {
  Wrench,
  Calendar,
  PauseCircle,
  CarFront,
  ThumbsDown,
  AlertTriangle
}
const SUB_STATUS_FILTER_ORDER = [
  SubStatusEnum.WIP,
  SubStatusEnum.SCHEDULED,
  SubStatusEnum.ON_HOLD,
  SubStatusEnum.TOTAL_LOSS,
  SubStatusEnum.REJECTED,
  SubStatusEnum.MISSED
]
const SubStatusFilterChips = ({ selected, counts, onChange }) => {
  const toggle = (value) => {
    const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
    onChange(next)
  }
  return (
    <AdasBox className='mb-5 hidden min-[435px]:flex flex-wrap items-center gap-2'>
      <span className='mr-1 text-[11px] font-bold uppercase tracking-wider text-[#475569]'>Sub-status</span>
      {SUB_STATUS_FILTER_ORDER.map((opt) => {
        const Icon = SUB_STATUS_FILTER_ICONS[opt.iconName]
        const isActive = selected.includes(opt.value)
        const count = counts?.[opt.value] ?? 0
        return (
          <button
            key={opt.value}
            type='button'
            onClick={() => toggle(opt.value)}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors ${
              isActive ? 'bg-[#0A1628] text-white' : 'bg-[#F5F7FA] text-[#2C3D4D] hover:bg-[#EBEDF0]'
            }`}
            aria-pressed={isActive}
          >
            <span className='inline-flex h-5 w-5 flex-none items-center justify-center rounded-full' style={{ backgroundColor: opt.tile }}>
              {Icon && <Icon className='h-3 w-3' style={{ color: opt.icon }} />}
            </span>
            {opt.label}
            <span
              className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                isActive ? 'bg-white/20 text-white' : 'bg-[#DBEAFE] text-[#1F6FEB]'
              }`}
            >
              {count}
            </span>
          </button>
        )
      })}
    </AdasBox>
  )
}
SubStatusFilterChips.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  counts: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

export const QuotePage = () => {
  useScrollTop()
  const location = useLocation()
  // URL is the source of truth for filter / pagination / search / search-filter
  // chips, so that goBack from a quote summary restores the exact same list
  // view and a shared link reproduces it.
  const queryParams = new URLSearchParams(location.search)
  const statusFilter = queryParams.get('status') || 'OPEN'
  const initialPageNo = Math.max(0, Number(queryParams.get('page') || 0))
  const initialPageSize = Math.max(1, Number(queryParams.get('size') || 25))
  const initialSearch = queryParams.get('q') || ''
  const initialPrimarySearchBy = queryParams.get('psb') || 'ro'
  const initialPrimaryQuery = queryParams.get('pq') || ''
  const initialSecondarySearchBy = queryParams.get('ssb') || 'vin'
  const initialSecondaryQuery = queryParams.get('sq') || ''
  const parseJsonParam = (raw) => {
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  const initialMultiFilters = parseJsonParam(queryParams.get('mf'))
  // CAR-3491 — sub-status chip row above the table. Multi-select OR within the
  // facet; comma-separated in the URL (`?ss=WIP,SCHEDULED`) for share-ability.
  const initialSubStatus = (queryParams.get('ss') || '').split(',').filter(Boolean)
  const [subStatusFilter, setSubStatusFilter] = useState(initialSubStatus)
  // Column sort (`?sort=&dir=`); null = newest first.
  const [sort, setSort] = useState(() => sortFromQuery(queryParams, QUOTE_SORT_KEYS))
  const [quoteList, setQuoteList] = useState([])
  // CAR-3677 — multi-select. `selected` is a Map(id -> quote) so we can drive the
  // ribbon off `selected.size` and still recover each row for single-record
  // actions (View PDF / Email) and the bulk Close.
  const [selected, setSelected] = useState(() => new Map())
  const [confirmClose, setConfirmClose] = useState(false)
  const [closeLoading, setCloseLoading] = useState(false)
  const [refreshIndex, setRefreshIndex] = useState(0)
  // CAR-3678 — the quote currently being booked into a New Appointment.
  const [appointmentQuote, setAppointmentQuote] = useState(null)
  const [convertLoading, setConvertLoading] = useState(false)
  const addMessage = useMessageStore((store) => store.addMessage)
  const singleQuote = selected.size === 1 ? Array.from(selected.values())[0] : null
  const [pageParams, setPageParams] = useState({
    pageNo: initialPageNo,
    totalPages: 0,
    totalElements: 0,
    pageSize: initialPageSize,
    status: statusFilter,
    search: initialSearch
  })
  const [isEmail, setIsEmail] = useState(false)
  const [emailContacts, setEmailContacts] = useState({})
  const [quotePdf, setQuotePdf] = useState()
  const [searchInput, setSearchInput] = useState(initialSearch)
  const [pdfLoader, setPdfLoader] = useState(false)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const ccId = useCenterStore((store) => store.ccId)
  const history = useHistory()
  const {
    primarySearchBy,
    setPrimarySearchBy,
    primaryQuery,
    setPrimaryQuery,
    secondarySearchBy,
    setSecondarySearchBy,
    secondaryQuery,
    setSecondaryQuery,
    multiFilters,
    setMultiFilters,
    searchFilters,
    searchOptions,
    resetSearchFilters
  } = useSearchFilters({
    identifierValue: 'quote',
    identifierLabel: 'Quote #',
    initialValues: {
      primarySearchBy: initialPrimarySearchBy,
      primaryQuery: initialPrimaryQuery,
      secondarySearchBy: initialSecondarySearchBy,
      secondaryQuery: initialSecondaryQuery,
      multiFilters: initialMultiFilters
    },
    onFiltersChange: () =>
      setPageParams((prev) => ({
        ...prev,
        pageNo: 0
      }))
  })

  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const filterOpen = useMobileToolbarStore((s) => s.filterOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  // Mirror the search box into the fetch params instantly (no debounce,
  // matching the Invoices queue search behavior).
  useEffect(() => {
    setPageParams((prev) => ({ ...prev, search: searchInput }))
  }, [searchInput])

  // Mirror status / pagination / search / search-filter chips into the URL via
  // history.replace so the back button on Quote Summary (history.goBack)
  // restores the exact same view, and a shared link reproduces it.
  useEffect(() => {
    const params = new URLSearchParams()
    if (pageParams.status) params.set('status', pageParams.status)
    if (pageParams.pageNo > 0) params.set('page', String(pageParams.pageNo))
    if (pageParams.pageSize && pageParams.pageSize !== 25) params.set('size', String(pageParams.pageSize))
    if (pageParams.search) params.set('q', pageParams.search)
    if (primarySearchBy && primarySearchBy !== 'ro') params.set('psb', primarySearchBy)
    if (primaryQuery) params.set('pq', primaryQuery)
    if (secondarySearchBy && secondarySearchBy !== 'vin') params.set('ssb', secondarySearchBy)
    if (secondaryQuery) params.set('sq', secondaryQuery)
    const activeMulti = Object.fromEntries(Object.entries(multiFilters || {}).filter(([, v]) => v != null && v !== ''))
    if (Object.keys(activeMulti).length > 0) params.set('mf', JSON.stringify(activeMulti))
    if (subStatusFilter && subStatusFilter.length > 0) params.set('ss', subStatusFilter.join(','))
    writeSortToQuery(params, sort)
    const next = params.toString()
    const current = location.search.replace(/^\?/, '')
    if (next !== current) {
      history.replace({ pathname: window.location.pathname, search: next ? `?${next}` : '' })
    }
  }, [
    pageParams.status,
    pageParams.pageNo,
    pageParams.pageSize,
    pageParams.search,
    primarySearchBy,
    primaryQuery,
    secondarySearchBy,
    secondaryQuery,
    multiFilters,
    subStatusFilter,
    sort,
    history,
    location.search
  ])

  // Client users read the queue; the create CTAs are not offered to them and the
  // new-quote route turns them away anyway.
  const isReadOnly = useIsReadOnly()

  const redirectToNewQuote = () => {
    history.push(REDIRECT_URLS.NEW_QUOTE)

    // Scroll to top of the page
    scrollPageTop()
  }

  const handleClick = (data) => {
    history.push(getQuoteSummaryUrl(data?.reference_number))
  }

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value)
  }, [])

  // Switching the status tab resets every active filter (free-text search +
  // search-by chips + sub-status chips) so the new tab starts from a clean,
  // unfiltered list.
  const toggleHandler = useCallback(
    (e) => {
      setSearchInput('')
      setSubStatusFilter([])
      resetSearchFilters()
      setPageParams((prev) => ({
        ...prev,
        status: e.target.value,
        pageNo: 0, // Reset to first page on filter change
        search: ''
      }))
    },
    [resetSearchFilters]
  )

  // A new sort starts from the first page; it survives status-tab and filter changes.
  const applySort = useCallback((nextValue) => {
    setSort(nextValue)
    setPageParams((prev) => ({ ...prev, pageNo: 0 }))
  }, [])
  const handleSort = useCallback((key) => applySort(nextSort(sort, key)), [applySort, sort])

  const paginationHandler = useCallback((event, page) => {
    setPageParams((prev) => {
      const updated = {
        ...prev,
        pageNo: page - 1
      }
      return updated
    })
  }, [])

  const pageSizeHandler = useCallback((event) => {
    setPageParams((prev) => {
      const updated = {
        ...prev,
        pageSize: event.target.value,
        pageNo: 0
      }
      return updated
    })
  }, [])

  const [subStatusCounts, setSubStatusCounts] = useState({})
  useEffect(() => {
    const quoteListResponse = getQuotelist({
      pageNo: pageParams.pageNo,
      pageSize: pageParams.pageSize,
      status: pageParams.status,
      ccid: ccId,
      search: pageParams.search,
      searchFilters,
      subStatus: subStatusFilter,
      ...sortRequestParams(sort)
    })
    setLoading(true)
    quoteListResponse.then((response) => {
      if (response?.status === 200) {
        setQuoteList(response.data.quotes)
        setPageParams((prev) => {
          const updated = {
            ...prev,
            totalPages: response.data.totalPages,
            totalElements: response.data.totalElements ?? response.data.quotes?.length ?? 0
          }
          return updated
        })
        // BE companion ticket adds a subStatusCounts map on the list response.
        // Fall back to {} so the chip row still renders with zeroes if the
        // field isn't present yet.
        if (response.data.subStatusCounts && typeof response.data.subStatusCounts === 'object') {
          setSubStatusCounts(response.data.subStatusCounts)
        }
        scrollPageTop()
      }
      setLoading(false)
    })
  }, [
    pageParams.pageNo,
    pageParams.pageSize,
    pageParams.status,
    pageParams.search,
    searchFilters,
    subStatusFilter,
    sort,
    ccId,
    setLoading,
    refreshIndex
  ])

  const emailQuoteHandler = useCallback(async () => {
    if (!singleQuote) return
    const params = {
      quoteId: singleQuote.id
    }
    try {
      const emailContactResponse = await getQuoteEmails(params)
      if (emailContactResponse.status === 200) {
        setIsEmail(true)
        setEmailContacts(emailContactResponse.data)
      }
      const quotePdfResponse = await getQuotePdf({
        quoteId: singleQuote.id
      })
      if (quotePdfResponse.status === 200) {
        const fileValue = new File([quotePdfResponse.data], `quote_${singleQuote.reference_number}.pdf`)
        fileValue.id = singleQuote.id
        setQuotePdf(fileValue)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { QuoteId: singleQuote.id, path: 'QuotePage/emailQuoteHandler' } })
    }
  }, [singleQuote])

  // View or print the selected quote's PDF.
  const quotePdfHandler = async (action = PDF_ACTION.VIEW) => {
    if (!singleQuote) return
    try {
      setPdfLoader(true)
      const quotePdfResponse = await getQuotePdf({
        quoteId: singleQuote.id
      })
      if (quotePdfResponse.status === 200) {
        handlePdf(quotePdfResponse.data, `quote_${singleQuote.reference_number}.pdf`, action)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { QuoteId: singleQuote.id, action, path: 'QuotePage/quotePdfHandler' } })
    } finally {
      setPdfLoader(false)
    }
  }
  const viewPdfHandler = () => quotePdfHandler(PDF_ACTION.VIEW)
  const printPdfHandler = () => quotePdfHandler(PDF_ACTION.PRINT)

  // Convert the selected open quote without opening it. A quote with no VIN goes to its summary
  // page instead, which asks for the VIN before converting.
  const canConvertToWorkOrder = useModuleAccess()('work_orders') && !isReadOnly
  const canConvertSelected = canConvertToWorkOrder && singleQuote && StatusEnum[singleQuote.status] === StatusEnum.OPEN
  const handleConvertToWorkOrder = async () => {
    if (!canConvertSelected) return
    const { id, reference_number: referenceNumber, vehicle } = singleQuote
    if (!vehicle?.vin) {
      history.push(getQuoteSummaryUrl(referenceNumber), { convertToWorkOrder: true })
      return
    }
    setConvertLoading(true)
    try {
      const response = await transformToWO({ quoteId: id })
      if (response.status === 200 || response.status === 201) {
        history.push(getWorkOrderSummaryUrl(referenceNumber))
      }
    } catch (error) {
      Logger.error({ message: error, payload: { QuoteId: id, path: 'QuotePage/handleConvertToWorkOrder' } })
      addMessage({ text: getFriendlyErrorMessage(error) })
    } finally {
      setConvertLoading(false)
    }
  }

  // CAR-3677 — bulk Close. Closes every selected quote, then clears the
  // selection and refetches the list.
  const handleCloseSelected = async () => {
    if (selected.size === 0) return
    setCloseLoading(true)
    try {
      await Promise.all(Array.from(selected.keys()).map((id) => closeQuote({ quoteId: id })))
      setSelected(new Map())
      setConfirmClose(false)
      setRefreshIndex((i) => i + 1)
    } catch (error) {
      Logger.error({ message: error, payload: { path: 'QuotePage/handleCloseSelected' } })
    } finally {
      setCloseLoading(false)
    }
  }

  // CAR-3678 — New Appointment CTA (quote queue, single-select only). Opens the
  // Scheduler modal prefilled from and linked to the selected quote.
  // CAR-4034 — after checking the quote isn't already booked (confirm dialog if it is).
  const { requestNewAppointment, guardDialog: existingAppointmentDialog } = useExistingAppointmentGuard(setAppointmentQuote)
  const handleNewAppointment = () => {
    if (singleQuote) requestNewAppointment(singleQuote)
  }
  const tabs = [
    { value: 'OPEN', label: 'Open' },
    { value: 'OPEN,CLOSED', label: 'All' },
    { value: 'CLOSED', label: 'Closed' }
  ]

  const toggleSelect = (item) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(item.id)) next.delete(item.id)
      else next.set(item.id, item)
      return next
    })
  }

  const clearSelection = () => setSelected(new Map())

  return (
    <div className='mx-auto max-w-[1400px] py-6'>
      <EmailForm
        emailType='quote'
        quotePdf={quotePdf}
        isOpen={isEmail}
        contacts={emailContacts}
        emailData={{ id: singleQuote?.id }}
        onClose={() => setIsEmail(false)}
      />

      {existingAppointmentDialog}

      {/* CAR-3678 — New Appointment, prefilled + linked to the selected quote */}
      {appointmentQuote && (
        <CreateAppointmentForm
          open={Boolean(appointmentQuote)}
          onClose={() => setAppointmentQuote(null)}
          prefill={buildAppointmentPrefillFromQuote(appointmentQuote)}
          linkedQuoteId={appointmentQuote.id}
          linkedQwiId={appointmentQuote.qwi_id}
          onSaved={() => {
            setSelected(new Map())
            setRefreshIndex((i) => i + 1)
          }}
        />
      )}

      {/* Page heading + top-right create CTA. Per CAR-3677 all record-level
          actions (View PDF / Email / New Appointment / Close) now live in the
          selection ribbon; only the page-level "New Quote" create stays here. */}
      <AdasBox className='hidden min-[435px]:flex pt-6 md:pt-4 mb-6 flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>Quotes</h1>
        <AdasBox className='hidden md:flex items-center gap-3'>
          {!isReadOnly && (
            <button
              onClick={redirectToNewQuote}
              className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-sidebar text-white text-sm font-medium rounded-lg hover:bg-sidebar/90 transition-colors'
            >
              <Plus className='h-4 w-4' />
              New Quote
            </button>
          )}
        </AdasBox>
      </AdasBox>

      {/* Mobile inline search bar */}
      {searchOpen && (
        <AdasBox className='min-[435px]:hidden mb-3 mt-2 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            placeholder='Client, VIN, RO, vehicle, quote #'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </AdasBox>
      )}

      {/* Underlined tabs with active count chip */}
      <AdasBox className='mb-4 flex items-center gap-7 border-b border-[#D1D5D8]'>
        {tabs.map(({ value, label }) => {
          const active = pageParams.status === value
          return (
            <button
              key={value}
              type='button'
              onClick={() => toggleHandler({ target: { value } })}
              className={`pb-3 -mb-px flex items-center gap-2 text-[14px] font-medium transition-colors ${
                active ? 'text-[#1F6FEB] border-b-2 border-[#1F6FEB]' : 'text-[#6B7280] hover:text-[#475569]'
              }`}
            >
              {label}
              {active && pageParams.totalElements > 0 && (
                <span className='inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#DBEAFE] text-[#1F6FEB] text-[11px] font-semibold'>
                  {pageParams.totalElements}
                </span>
              )}
            </button>
          )
        })}
      </AdasBox>

      {/* Sub-status filter chips — multi-select OR within facet, AND with the
          main-status tabs above. Renders on every tab; counts come from the BE
          companion ticket (CAR-3491). */}
      <SubStatusFilterChips selected={subStatusFilter} counts={subStatusCounts} onChange={setSubStatusFilter} />

      {/* Filter row */}
      <AdasBox className='mb-5 hidden min-[435px]:flex flex-col md:flex-row items-start md:items-center gap-3'>
        <SearchWithSelect
          selectValue={primarySearchBy}
          onSelectChange={(e) => setPrimarySearchBy(e.target.value)}
          searchValue={primaryQuery}
          onSearchChange={(e) => setPrimaryQuery(e.target.value)}
          options={searchOptions}
          placeholder='Search...'
        />
        <SearchWithSelect
          selectValue={secondarySearchBy}
          onSelectChange={(e) => setSecondarySearchBy(e.target.value)}
          searchValue={secondaryQuery}
          onSearchChange={(e) => setSecondaryQuery(e.target.value)}
          options={searchOptions}
          placeholder='Search...'
        />
      </AdasBox>

      {/* Mobile selection action bar — between tabs and card list. Single-record
          actions (Work Order / Schedule / PDF / Print / Email) show only when exactly
          one row is selected, in the same order as the desktop ribbon; Close + Clear
          are always available (CAR-3677). */}
      {selected.size > 0 && (
        <div
          className='min-[435px]:hidden -mx-3 mb-3 flex items-start justify-between gap-3 px-4 py-3 text-white'
          style={{ background: 'var(--primary-gradient)' }}
        >
          <span className='py-1.5 text-[14px] font-semibold whitespace-nowrap'>{selected.size} selected</span>
          <div className='flex flex-wrap items-center justify-end gap-2'>
            {singleQuote ? (
              <>
                {canConvertSelected && (
                  <button
                    onClick={handleConvertToWorkOrder}
                    disabled={convertLoading}
                    className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40'
                  >
                    {convertLoading ? <AdasCircularProgress color='inherit' size={14} /> : <Wrench className='h-3.5 w-3.5' />}
                    Work Order
                  </button>
                )}
                {/* Booking an appointment is the one write a client user may make. */}
                <button
                  onClick={handleNewAppointment}
                  className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <Calendar className='h-3.5 w-3.5' />
                  Schedule
                </button>
                {!isReadOnly && (
                  <button
                    onClick={() => setConfirmClose(true)}
                    disabled={closeLoading}
                    className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40'
                  >
                    Close
                  </button>
                )}
                <button
                  onClick={viewPdfHandler}
                  disabled={pdfLoader}
                  className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40'
                >
                  {pdfLoader ? <AdasCircularProgress color='inherit' size={14} /> : <Download className='h-3.5 w-3.5' />}
                  PDF
                </button>
                <button
                  onClick={printPdfHandler}
                  disabled={pdfLoader}
                  className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40'
                >
                  <Printer className='h-3.5 w-3.5' />
                  Print
                </button>
                {!isReadOnly && (
                  <button
                    onClick={emailQuoteHandler}
                    className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                  >
                    <Mail className='h-3.5 w-3.5' />
                    Email
                  </button>
                )}
              </>
            ) : (
              !isReadOnly && (
                <button
                  onClick={() => setConfirmClose(true)}
                  disabled={closeLoading}
                  className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40'
                >
                  Close
                </button>
              )
            )}
            <button onClick={clearSelection} className='text-[13px] font-semibold text-white/80 hover:text-white'>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Mobile card list */}
      <div className='min-[435px]:hidden pt-2 space-y-3'>
        {quoteList.length === 0 ? (
          <div className='rounded-xl border border-[#E5E7EB] bg-white p-8 text-center text-[13px] text-[#6B7280]'>No quotes found</div>
        ) : (
          quoteList.map((item) => (
            <QueueCard
              key={item.id}
              item={item}
              itemType='Q'
              isSelected={selected.has(item.id)}
              onSelect={toggleSelect}
              onClick={handleClick}
              assigneeType='quotes'
            />
          ))
        )}
      </div>

      {/* Desktop selection ribbon — sticky to the top of the scroll area as the
          table scrolls. Rendered as a direct child of the page root (not wrapped)
          so the sticky element has room to travel; responsive hide lives on the
          ribbon itself (CAR-3677). */}
      <QueueSelectionRibbon
        className='hidden min-[435px]:flex'
        count={selected.size}
        recordLabelPlural='Quotes'
        showNewAppointment
        onViewPdf={viewPdfHandler}
        onPrint={printPdfHandler}
        onEmail={emailQuoteHandler}
        onNewAppointment={handleNewAppointment}
        onConvertToWorkOrder={canConvertSelected ? handleConvertToWorkOrder : undefined}
        convertLoading={convertLoading}
        onClose={() => setConfirmClose(true)}
        onClear={clearSelection}
        pdfLoading={pdfLoader}
        closeLoading={closeLoading}
      />

      {/* Quotes table */}
      <div className='hidden min-[435px]:block overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
        <Table>
          <TableHeader>
            <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
              <TableHead className='w-12 text-white' aria-label='Selection' />
              <SortableTableHead
                label='Quote'
                sortKey='referenceNumber'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Client'
                sortKey='client'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <TableHead
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
                aria-sort={sort?.key === 'assignee' || sort?.key === 'status' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <span className='inline-flex items-center gap-1'>
                  <SortButton label='Assignee' sortKey='assignee' sort={sort} onSort={handleSort} />
                  <span aria-hidden='true'>&amp;</span>
                  <SortButton label='Status' sortKey='status' sort={sort} onSort={handleSort} />
                </span>
              </TableHead>
              <SortableTableHead
                label='Amount'
                sortKey='amount'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Created'
                sortKey='createdAt'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {quoteList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className='py-10 text-center text-[13px] text-[#6B7280]'>
                  No quotes found
                  {pageParams.totalPages > 1 && ' on this page. Go to next page to search more quotes.'}
                </TableCell>
              </TableRow>
            ) : (
              quoteList.map((item) => {
                const isOpen = StatusEnum[item.status] === StatusEnum.OPEN
                const isSelected = selected.has(item.id)
                return (
                  <TableRow
                    key={item.id}
                    data-state={isSelected ? 'selected' : undefined}
                    onClick={() => handleClick(item)}
                    className='cursor-pointer h-[72px] even:bg-[#F8FAFC] hover:bg-[#F0F4FF] data-[state=selected]:bg-[#E8EFFF] data-[state=selected]:hover:bg-[#E8EFFF]'
                  >
                    <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(item)}
                        aria-label={`Select quote ${item.reference_number}`}
                      />
                    </TableCell>
                    <TableCell className='relative align-middle pr-10 whitespace-nowrap'>
                      {/* AI mark pinned to the reserved right gutter and vertically
                          centered, so it lands at the same spot on every row
                          regardless of whether the row has a VIN line. */}
                      {(item.source === 'ADAS_INTELLIGENCE' || item.ai_processed) && (
                        <img
                          src={adasIntelligenceMark}
                          alt='ADAS Intelligence'
                          title='Created from ADAS Intelligence'
                          className='absolute right-4 top-1/2 h-3 w-auto -translate-y-1/2 object-contain'
                        />
                      )}
                      <div className='min-w-[280px]'>
                        <div className='flex items-center gap-x-1.5 text-[12px] text-[#6B7280] whitespace-nowrap'>
                          <span>Quote #{item.reference_number}</span>
                          {item.repair_order_num && (
                            <>
                              <span className='text-[#CBD5E1]'>|</span>
                              <span>RO #{item.repair_order_num}</span>
                            </>
                          )}
                          {item.type && (
                            <>
                              <span className='text-[#CBD5E1]'>|</span>
                              <span>{item.type === 'SERVICE_ONLY' ? 'Service' : item.type === 'STANDARD' ? 'Standard' : item.type}</span>
                            </>
                          )}
                          {item.insurance_company?.name && (
                            <>
                              <span className='text-[#CBD5E1]'>|</span>
                              <span>{item.insurance_company.name}</span>
                            </>
                          )}
                        </div>
                        <div className='mt-1 text-[15px] font-bold uppercase tracking-tight text-[#0A1628] whitespace-nowrap'>
                          {[item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ') || '—'}
                        </div>
                        {item.vehicle?.vin && (
                          <div className='mt-0.5 font-mono text-[11px] text-[#6B7280] tracking-wider whitespace-nowrap'>
                            {item.vehicle.vin}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#0A1628]'>{item.client?.name || '—'}</TableCell>
                    <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                      <div className='flex items-center gap-3'>
                        <AssigneeAvatarPicker
                          workOrderId={item.id}
                          initialAssignee={item.assigned_to}
                          closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                        />
                        <div className='flex flex-col items-start gap-1'>
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${
                              isOpen ? 'bg-[#E8EFFF] text-[#2C3D4D]' : 'bg-[#EBEDF0] text-[#6B7280]'
                            }`}
                          >
                            <span className={`inline-block h-1.5 w-1.5 rounded-full ${isOpen ? 'bg-[#2C3D4D]' : 'bg-[#6B7280]'}`} />
                            {StatusEnum[item.status]}
                          </span>
                          <SubStatusPicker
                            quoteId={item.id}
                            currentSubStatus={item.sub_status}
                            closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                            setBy={item.sub_status_set_by}
                            setAt={item.sub_status_set_at}
                            onChange={(next) => {
                              // Optimistic local update so the strike-through / pill reflect
                              // the new value without a full refetch.
                              setQuoteList((list) => list.map((q) => (q.id === item.id ? { ...q, sub_status: next } : q)))
                            }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className='align-middle'>
                      {item.locked ? (
                        <LockedValue title='Locked by your plan — upgrade to see this quote'>$1,234</LockedValue>
                      ) : LOST_SUB_STATUSES.includes(item.sub_status) ? (
                        <div className='flex flex-col items-start'>
                          <span className='text-[15px] font-bold text-[#94A3B8] line-through'>
                            {currencyFormatter(item.pre_rebate_total ?? item.total_cost)}
                          </span>
                          <span className='mt-0.5 text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]'>Rejected</span>
                        </div>
                      ) : (
                        <span className='text-[15px] font-bold text-[#0A1628]'>
                          {currencyFormatter(item.pre_rebate_total ?? item.total_cost)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#6B7280]'>
                      {item.created_at
                        ? (() => {
                            const d = new Date(item.created_at)
                            const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                            const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                            return `${date} ${time}`
                          })()
                        : ''}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile floating "+ New Quote" CTA */}
      {!isReadOnly && (
        <button
          onClick={redirectToNewQuote}
          className='min-[435px]:hidden fixed bottom-5 right-5 z-30 inline-flex items-center gap-1.5 rounded-full bg-sidebar px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-sidebar/90'
        >
          <Plus className='h-4 w-4' />
          New Quote
        </button>
      )}

      {/* Mobile filter bottom drawer — primary + secondary SearchWithSelect (matches desktop) */}
      <MobileFilterDrawer
        open={filterOpen}
        onClose={closeMobileToolbar}
        primarySearchBy={primarySearchBy}
        setPrimarySearchBy={setPrimarySearchBy}
        primaryQuery={primaryQuery}
        setPrimaryQuery={setPrimaryQuery}
        secondarySearchBy={secondarySearchBy}
        setSecondarySearchBy={setSecondarySearchBy}
        secondaryQuery={secondaryQuery}
        setSecondaryQuery={setSecondaryQuery}
        searchOptions={searchOptions}
        onClear={() => setMultiFilters({})}
        sortOptions={QUOTE_SORT_OPTIONS}
        sort={sort}
        onSortChange={applySort}
      />

      <AdasPaper elevation={0} className='hidden min-[435px]:flex items-center justify-between p-[20px] mt-[20px]'>
        <AdasBox className='w-[20%]'>
          <ItemPerPageSelector itemPerPageList={[25, 50, 100, 200]} pageSizeHandler={pageSizeHandler} pageSize={pageParams.pageSize} />
        </AdasBox>
        <AdasBox>
          <AdasStack spacing={2}>
            <AdasPagination onChange={paginationHandler} page={pageParams.pageNo + 1} count={pageParams.totalPages} shape='rounded' />
          </AdasStack>
        </AdasBox>
      </AdasPaper>

      {/* CAR-3677 — confirm before closing the selected quote(s) */}
      <ConfirmModal
        open={confirmClose}
        handleClose={() => setConfirmClose(false)}
        handleContinue={handleCloseSelected}
        title={`Close ${selected.size > 1 ? `${selected.size} Quotes` : 'Quote'}?`}
        message={`Are you sure you want to close ${selected.size > 1 ? `these ${selected.size} quotes` : 'this quote'}? This moves ${
          selected.size > 1 ? 'them' : 'it'
        } to the Closed tab.`}
        confirmLabel={BUTTONS_DISPLAY_LABEL.CONFIRM}
      />
    </div>
  )
}
